import type { Edge, Node } from '@xyflow/react'
import { MarkerType } from '@xyflow/react'
import type { FlowConfigData, FlowStep, FlowStepConfig } from '../../types/flowConfig.types'
import { getDefaultFlowStepConfig } from '../../utils/flowStepDefaults'
import { pruneUiLayoutToSteps } from '../../utils/flowUiLayout'

/** Dados do nó customizado no canvas. */
export interface FlowCanvasNodeData extends Record<string, unknown> {
  label: string
  stepType: string
  isStart: boolean
  sourceHandles: Array<{ id: string; shortLabel: string }>
  /** Só no canvas editável: define `start_step` para este nó. */
  onSetAsStart?: () => void
}

export interface FlowOutgoingLink {
  target: string
  sourceHandle: string
  label: string
}

const HANDLE_LABELS: Record<string, string> = {
  next: '→',
  error: 'Erro',
  true: 'Sim',
  false: 'Não',
  default: 'Padrão',
  base: 'Base',
  item: 'Item'
}

function shortLabelForHandle(id: string, index?: number): string {
  if (HANDLE_LABELS[id]) return HANDLE_LABELS[id]
  if (id.startsWith('cond-')) return `#${(index ?? 0) + 1}`
  return id
}

/**
 * Ligações de saída de uma etapa (alinhado a `next_step`, `error_step`, condicionais, etc.).
 */
export function getOutgoingLinks(
  _stepId: string,
  step: FlowStep,
  allStepIds: Set<string>
): FlowOutgoingLink[] {
  const links: FlowOutgoingLink[] = []
  const seen = new Set<string>()

  const add = (target: string | undefined | null, sourceHandle: string, label: string) => {
    if (!target || typeof target !== 'string' || !allStepIds.has(target)) return
    const key = `${sourceHandle}->${target}`
    if (seen.has(key)) return
    seen.add(key)
    links.push({ target, sourceHandle, label })
  }

  add(step.next_step, 'next', 'Próximo')
  add(step.error_step, 'error', 'Erro')

  const cfg = (step.config || {}) as FlowStepConfig

  switch (step.type) {
    case 'conditional':
      add(cfg.true_step, 'true', 'Sim (verdadeiro)')
      add(cfg.false_step, 'false', 'Não (falso)')
      add(cfg.default_step, 'default', 'Padrão')
      ;(cfg.conditions || []).forEach((c, i) => {
        add(c.next_step, `cond-${i}`, `Condição ${i + 1}`)
      })
      break
    case 'dynamic_conditional': {
      const d = cfg.dynamic_conditions
      if (d) {
        add(d.base_next_step, 'base', 'Base / finalizar')
        add(d.item_next_step, 'item', 'Item')
        add(d.default_step, 'default', 'Padrão')
      }
      break
    }
    default:
      break
  }

  return links
}

/**
 * Handles de saída sempre disponíveis no canvas para edição (mesmo sem ligação ainda).
 */
export function getDefaultHandleIdsForStepType(step: FlowStep): string[] {
  switch (step.type) {
    case 'conditional': {
      const n = Math.max(1, step.config?.conditions?.length ?? 0)
      const ids: string[] = ['true', 'false', 'default']
      for (let i = 0; i < n; i++) ids.push(`cond-${i}`)
      return ids
    }
    case 'dynamic_conditional':
      return ['base', 'item', 'default']
    case 'finalization':
      return []
    default:
      return ['next', 'error']
  }
}

function sortHandleIdsForDisplay(ids: string[]): string[] {
  const rank = (id: string): number => {
    if (id === 'next') return 0
    if (id === 'error') return 1
    if (id === 'true') return 2
    if (id === 'false') return 3
    if (id === 'default') return 4
    if (id === 'base') return 5
    if (id === 'item') return 6
    if (id.startsWith('cond-')) return 10 + parseInt(id.replace('cond-', ''), 10)
    return 50
  }
  return [...ids].sort((a, b) => rank(a) - rank(b))
}

/**
 * União de ligações existentes + handles editáveis por tipo (para o StepNode).
 */
export function getCanvasSourceHandles(
  stepId: string,
  step: FlowStep,
  allStepIds: Set<string>
): Array<{ id: string; shortLabel: string }> {
  const idSet = new Set<string>()
  for (const l of getOutgoingLinks(stepId, step, allStepIds)) {
    idSet.add(l.sourceHandle)
  }
  for (const id of getDefaultHandleIdsForStepType(step)) {
    idSet.add(id)
  }
  return sortHandleIdsForDisplay([...idSet]).map((id) => ({
    id,
    shortLabel: shortLabelForHandle(
      id,
      id.startsWith('cond-') ? parseInt(id.replace('cond-', ''), 10) : undefined
    )
  }))
}

function clearStepOutgoing(step: FlowStep): void {
  const s = step
  delete s.next_step
  delete s.error_step
  if (!s.config) s.config = {}
  const cfg = s.config as FlowStepConfig
  switch (s.type) {
    case 'conditional':
      delete cfg.true_step
      delete cfg.false_step
      delete cfg.default_step
      if (cfg.conditions?.length) {
        cfg.conditions = cfg.conditions.map((c) => ({ ...c, next_step: '' }))
      }
      break
    case 'dynamic_conditional':
      if (cfg.dynamic_conditions) {
        const d = { ...cfg.dynamic_conditions }
        delete d.base_next_step
        delete d.item_next_step
        delete d.default_step
        cfg.dynamic_conditions = d
      }
      break
    default:
      break
  }
}

function applyOutgoingLink(step: FlowStep, handle: string, targetId: string): void {
  const h = handle || 'next'
  switch (step.type) {
    case 'conditional': {
      const cfg = step.config as FlowStepConfig
      if (h === 'true') {
        cfg.true_step = targetId
        return
      }
      if (h === 'false') {
        cfg.false_step = targetId
        return
      }
      if (h === 'default') {
        cfg.default_step = targetId
        return
      }
      if (h.startsWith('cond-')) {
        const idx = parseInt(h.replace('cond-', ''), 10)
        if (!cfg.conditions) cfg.conditions = []
        while (cfg.conditions.length <= idx) {
          cfg.conditions.push({ condition: '', template: '', next_step: '' })
        }
        cfg.conditions[idx] = { ...cfg.conditions[idx], next_step: targetId }
      }
      return
    }
    case 'dynamic_conditional': {
      const cfg = step.config as FlowStepConfig
      if (!cfg.dynamic_conditions) cfg.dynamic_conditions = {}
      const d = cfg.dynamic_conditions
      if (h === 'base') d.base_next_step = targetId
      else if (h === 'item') d.item_next_step = targetId
      else if (h === 'default') d.default_step = targetId
      return
    }
    default:
      if (h === 'next') step.next_step = targetId
      else if (h === 'error') step.error_step = targetId
  }
}

/**
 * Reconstrói `steps` a partir das arestas (Fase 2). Limpa saídas e aplica cada aresta.
 */
export function graphToFlowConfig(
  base: FlowConfigData,
  nodes: Node[],
  edges: Edge[],
  startStep: string
): FlowConfigData {
  const out = structuredClone(base) as FlowConfigData
  out.start_step = startStep
  out.ui_layout = { ...(out.ui_layout || {}), ...nodesToUiLayout(nodes) }
  const steps = out.steps || {}
  for (const id of Object.keys(steps)) {
    clearStepOutgoing(steps[id])
  }
  for (const e of edges) {
    const src = steps[e.source]
    const tgtId = e.target
    if (!src || !steps[tgtId]) continue
    const handle = e.sourceHandle || 'next'
    applyOutgoingLink(src, handle, tgtId)
  }
  return pruneUiLayoutToSteps(out)
}

/**
 * Adiciona uma nova etapa ao fluxo (paleta mínima no modo visual).
 * @param forcedStepId — se definido (ex.: gerado no handler de clique), evita colisões e torna re-aplicação idempotente.
 */
export function addStepToFlowConfig(
  base: FlowConfigData,
  type: FlowStep['type'],
  position: { x: number; y: number },
  idPrefix = 'step',
  forcedStepId?: string
): FlowConfigData {
  const stepId =
    forcedStepId ?? `${idPrefix}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
  const prevSteps = base.steps || {}
  if (prevSteps[stepId]) {
    return base
  }
  const steps = { ...prevSteps }
  const n = Object.keys(steps).length
  const newStep: FlowStep = {
    id: stepId,
    type,
    name: `Nova etapa ${n + 1}`,
    description: '',
    config: getDefaultFlowStepConfig(type)
  }
  steps[stepId] = newStep
  const ui_layout = { ...(base.ui_layout || {}), [stepId]: position }
  let start_step = base.start_step
  if (!start_step || !steps[start_step]) {
    start_step = stepId
  }
  return pruneUiLayoutToSteps({ ...base, steps, start_step, ui_layout })
}

function gridPosition(index: number, total: number): { x: number; y: number } {
  const cols = Math.max(1, Math.ceil(Math.sqrt(Math.max(total, 1))))
  const row = Math.floor(index / cols)
  const col = index % cols
  const gapX = 280
  const gapY = 120
  return { x: col * gapX, y: row * gapY }
}

/**
 * Converte `FlowConfigData` em nós e arestas para @xyflow/react.
 */
export function flowConfigToGraph(config: FlowConfigData): {
  nodes: Node<FlowCanvasNodeData>[]
  edges: Edge[]
} {
  const steps = config.steps || {}
  const ids = new Set(Object.keys(steps))
  const layout = config.ui_layout || {}
  const entries = Object.entries(steps)

  const nodes: Node<FlowCanvasNodeData>[] = entries.map(([id, step], index) => {
    const sourceHandles = getCanvasSourceHandles(id, step, ids)

    const pos = layout[id] ?? gridPosition(index, entries.length)

    return {
      id,
      type: 'flowStep',
      position: pos,
      data: {
        label: step.name || id,
        stepType: step.type,
        isStart: id === config.start_step,
        sourceHandles
      }
    }
  })

  const edges: Edge[] = []
  entries.forEach(([id, step]) => {
    getOutgoingLinks(id, step, ids).forEach((link) => {
      edges.push({
        id: `e-${id}-${link.target}-${link.sourceHandle}`,
        source: id,
        target: link.target,
        sourceHandle: link.sourceHandle,
        label: link.label,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#94a3b8' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' }
      })
    })
  })

  return { nodes, edges }
}

/**
 * Extrai `ui_layout` a partir dos nós após arrastar (Fase 1).
 */
export function nodesToUiLayout(nodes: Node[]): Record<string, { x: number; y: number }> {
  const out: Record<string, { x: number; y: number }> = {}
  for (const n of nodes) {
    out[n.id] = { x: n.position.x, y: n.position.y }
  }
  return out
}
